import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

/**
 * Status document interface
 *
 * NOTE: Keep this in sync with `src/lib/db/rxdb/schemas/status.ts::StatusDocument`.
 * The schema file is the source of truth for the persisted shape; this type
 * exists so `createEntityHooks` can generically bind to the collection.
 */
export interface StatusDocument extends BaseDocument {
  teamId: string
  /**
   * Projects this status is scoped to. Empty = global (visible to all projects);
   * non-empty = restricted to exactly those projects. See schemas/status.ts for
   * the visibility rules enforced on the backend.
   */
  projectIds: Array<string>
  name: string
  progressType: string
  description: string
  sortOrder: string
  isSystemGenerated: boolean
  isDefault: boolean
}

/**
 * Status-specific hooks
 */
const hooks = createEntityHooks<StatusDocument>('statuses')

export const useStatuses = (filter?: Partial<StatusDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  return { statuses: data, isLoading, db }
}

export const useStatus = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { status: data, isLoading }
}

export const useStatusMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createStatus: create,
    updateStatus: update,
    deleteStatus: deleteEntity,
  }
}
